/**
 * V10 Phase C — Episode 목록의 단일 row.
 *
 * - `occurredAt` + `content` (2줄 truncate)
 * - `source` 배지 + `otherLinkedEdges` 칩 (5 초과 시 "+N more")
 * - invalidated 면 opacity 0.3 + strikethrough + "(이 연결 비활성 · YYYY-MM-DD)" 보조 텍스트
 * - 우측 icon 버튼:
 *   - 🔗 "이 연결 끊기" → `EdgeInvalidateLinkModal` (countdown 2)
 *   - ⚠️ "이 사건 철회" → `EpisodeInvalidateModal` (countdown 3 + reason 필수)
 */
import { useState } from "react"
import type { EpisodeListItem } from "../../../api/admin-client.ts"

type Props = {
	readonly episode: EpisodeListItem
	readonly onInvalidateLink: () => void
	readonly onInvalidateEpisode: () => void
	/** relation name → 한국어 displayName. 미전달 시 raw relation 노출 */
	readonly relationDisplayNames?: ReadonlyMap<string, string>
}

/** 대략 2줄 넘을 때를 가늠하는 휴리스틱 — 이 길이 이상이면 "전체 보기" 토글을 노출한다. */
const LONG_CONTENT_THRESHOLD = 140

export function EpisodeListRow(props: Props) {
	const { episode, relationDisplayNames } = props
	const [menuOpen, setMenuOpen] = useState(false)
	const [expanded, setExpanded] = useState(false)
	const isLinkInvalid = episode.linkInvalidatedAt !== undefined
	const isEpisodeInvalid = episode.invalidatedAt !== undefined
	const isInvalid = isLinkInvalid || isEpisodeInvalid
	const isLong = episode.content.length > LONG_CONTENT_THRESHOLD || episode.content.includes("\n")

	const rowStyle: React.CSSProperties = {
		display: "flex",
		// flex 의 기본 align-items: stretch 면 buttons wrapper 가 row 전체 높이로 늘어나
		// `top: 100%` 드롭다운이 row 바닥까지 내려가 검색 창 근처에 떠버린다.
		// flex-start 로 고정해 wrapper 를 버튼 크기만큼만 차지하게 한다.
		alignItems: "flex-start",
		gap: "var(--pk-space-3)",
		padding: "var(--pk-space-3) 0",
		borderBottom: "1px solid var(--pk-border-subtle)",
		opacity: isInvalid ? 0.35 : 1,
		textDecoration: isInvalid ? "line-through" : "none"
	}

	return (
		<div style={rowStyle}>
			<div style={{ flex: 1, minWidth: 0 }}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "var(--pk-space-2)",
						marginBottom: "var(--pk-space-1)"
					}}
				>
					<time
						style={{
							fontSize: "0.75rem",
							color: "var(--pk-text-muted)",
							fontFamily: "var(--pk-font-mono)"
						}}
					>
						{new Date(episode.occurredAt).toLocaleDateString("ko-KR", {
							year: "numeric",
							month: "2-digit",
							day: "2-digit"
						})}
					</time>
					<span
						className="badge"
						style={{
							fontSize: "0.7rem",
							padding: "2px 6px",
							background: "var(--pk-bg-surface)",
							border: "1px solid var(--pk-border)",
							borderRadius: "var(--pk-radius-xs)"
						}}
					>
						{episode.source}
					</span>
				</div>
				<div
					style={{
						fontSize: "0.85rem",
						lineHeight: 1.5,
						color: "var(--pk-text-primary)",
						whiteSpace: "pre-wrap",
						wordBreak: "break-word",
						...(expanded
							? {}
							: {
									display: "-webkit-box",
									WebkitLineClamp: 2,
									WebkitBoxOrient: "vertical",
									overflow: "hidden"
								})
					}}
				>
					{episode.content}
				</div>
				{isLong && (
					<button
						type="button"
						onClick={() => setExpanded((prev) => !prev)}
						aria-expanded={expanded}
						style={{
							marginTop: "var(--pk-space-1)",
							padding: "2px 8px",
							fontSize: "0.72rem",
							color: "var(--pk-text-muted)",
							background: "transparent",
							border: "1px solid var(--pk-border-subtle)",
							borderRadius: "var(--pk-radius-pill)",
							cursor: "pointer",
							textDecoration: "none"
						}}
					>
						{expanded ? "▲ 접기" : `▼ 전체 보기 (${episode.content.length}자)`}
					</button>
				)}
				{episode.otherLinkedEdges.length > 0 && (
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							gap: "var(--pk-space-1)",
							marginTop: "var(--pk-space-2)",
							fontSize: "0.7rem"
						}}
					>
						<span style={{ color: "var(--pk-text-muted)", marginRight: "var(--pk-space-1)" }}>
							함께 쓰인 관계
						</span>
						{episode.otherLinkedEdges.slice(0, 5).map((ek) => {
							const rel = ek.split("|")[1] ?? ek
							const relLabel = relationDisplayNames?.get(rel) ?? rel
							return (
								<span
									key={ek}
									style={{
										padding: "1px 6px",
										borderRadius: "var(--pk-radius-pill)",
										background: "var(--pk-accent-subtle)",
										color: "var(--pk-accent)",
										fontFamily: "var(--pk-font-mono)"
									}}
									title={rel}
								>
									{relLabel}
								</span>
							)
						})}
						{episode.otherLinkedEdges.length > 5 && (
							<span
								style={{
									padding: "1px 6px",
									borderRadius: "var(--pk-radius-pill)",
									background: "var(--pk-bg-surface)",
									color: "var(--pk-text-muted)"
								}}
							>
								+{episode.otherLinkedEdges.length - 5} 더보기
							</span>
						)}
					</div>
				)}
				{isInvalid && (
					<div
						style={{
							marginTop: "var(--pk-space-1)",
							fontSize: "0.7rem",
							color: "var(--pk-text-muted)",
							textDecoration: "none"
						}}
					>
						{isEpisodeInvalid
							? `이 사건 철회 · ${new Date(episode.invalidatedAt ?? "").toLocaleDateString("ko-KR")}`
							: `이 연결 비활성 · ${new Date(episode.linkInvalidatedAt ?? "").toLocaleDateString("ko-KR")}`}
					</div>
				)}
			</div>

			<div style={{ flexShrink: 0, position: "relative" }}>
				{!isInvalid && (
					<>
						<button
							type="button"
							className="btn btn-ghost btn-sm"
							onClick={props.onInvalidateLink}
							title="이 연결 끊기 (이 edge 에서만 비활성)"
							aria-label="이 연결 끊기"
							style={{ padding: "4px 8px", fontSize: "0.9rem" }}
						>
							🔗
						</button>
						<button
							type="button"
							className="btn btn-ghost btn-sm"
							onClick={() => setMenuOpen(!menuOpen)}
							title="더 보기"
							aria-label="더 보기"
							style={{ padding: "4px 8px", fontSize: "0.9rem" }}
						>
							⋯
						</button>
						{menuOpen && (
							<div
								role="menu"
								style={{
									position: "absolute",
									top: "100%",
									right: 0,
									marginTop: "var(--pk-space-1)",
									background: "var(--pk-bg-elevated)",
									border: "1px solid var(--pk-border)",
									borderRadius: "var(--pk-radius-md)",
									boxShadow: "var(--pk-shadow-md)",
									zIndex: 10,
									minWidth: 180
								}}
							>
								<button
									type="button"
									onClick={() => {
										setMenuOpen(false)
										props.onInvalidateEpisode()
									}}
									style={{
										display: "block",
										width: "100%",
										textAlign: "left",
										padding: "var(--pk-space-2) var(--pk-space-3)",
										background: "transparent",
										border: "none",
										cursor: "pointer",
										fontSize: "0.85rem",
										color: "var(--pk-danger)"
									}}
								>
									⚠️ 이 사건 철회
								</button>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	)
}
